import { getIn, useFormikContext } from 'formik'
import { FieldConfig as FormikFieldConfig } from 'formik/dist/Field'
import React, { type JSX } from 'react'

import { Body2 } from '@ui/Typography'

import { useWindowResize } from '@utils/domUtils'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { useCurrencyAmountRate } from '../currency/CurrencyAmountRate'
import { DeepNonNullable } from '../types/DeepNonNullable'
import { DeepRequired } from '../types/DeepRequired'
import { TypedFieldProps } from '../types/TypedFieldProps'
import { ValidateField } from '../types/ValidateField'
import {
  createFormNumericField,
  isNumericFieldMinValid,
  isNumericFieldRangeValid,
} from './FormNumericField'

const FormNumericField = createFormNumericField()

type FieldConfig = Omit<FormikFieldConfig, 'name'>
interface FormFieldProps extends FieldConfig {
  namePrefix?: string
  fromCurrency: string
  toCurrency?: string
  leftText?: string
  onBlur?: (value: React.ChangeEvent<HTMLInputElement>) => void
  min?: number
  max?: number
  onChange?(value: React.ChangeEvent<HTMLInputElement>): void
  onClick?(): void
  hint?: string
}
export function RawCurrencyAmountRateField<FormValues>(
  props: FormFieldProps & TypedFieldProps<FormValues>
): JSX.Element {
  const { name, label, placeholder, fromCurrency, onBlur, toCurrency, hint, ...restProps } = props
  const { formatMoney } = useFormatNumber()
  const { values, errors } = useFormikContext()
  const isMobile = useWindowResize()
  const value = getIn(values, name)
  const error = getIn(errors, name)

  const { amount = 0 } = useCurrencyAmountRate({
    fromCurrency,
    toCurrency,
    amount: value,
  })

  const getNumericFieldHint = (): string | undefined => {
    const { min = 0, max, toCurrency } = props

    if (hint) {
      return hint
    }

    if (!(amount > 0)) {
      return undefined
    }

    if (error) {
      return undefined
    }

    if ((max && isNumericFieldRangeValid(value, min, max)) || !isNumericFieldMinValid(value, min)) {
      return `≈ ${formatMoney(amount, toCurrency)}`
    }
  }

  const hideLabelDueToPlaceholder =
    !value && !!placeholder && !!label && placeholder.trim() === label.trim()

  const numericFieldHint = getNumericFieldHint()

  return (
    <FormNumericField
      {...restProps}
      name={name}
      onBlur={onBlur}
      label={hideLabelDueToPlaceholder ? undefined : label}
      placeholder={placeholder}
      min={0}
      hint={
        isMobile && numericFieldHint ? (
          <Body2 color='text-purple-high-em'>{numericFieldHint}</Body2>
        ) : (
          hint
        )
      }
      rightText={!isMobile ? numericFieldHint : undefined}
    />
  )
}

export function createFormCurrencyAmountField<FormValues>() {
  return function TypedField<Name extends ValidateField<DeepNonNullable<DeepRequired<FormValues>>>>(
    props: FormFieldProps & TypedFieldProps<DeepNonNullable<DeepRequired<FormValues>>, Name>
  ): JSX.Element {
    return <RawCurrencyAmountRateField {...props} />
  }
}
